/* Insertion affordance rendered in a gap between two cards (or rail rows).
   `.zone` is a fixed 24px, in-flow element for BOTH variants — it carries
   all of the surrounding VStack's inter-row spacing itself
   (script_builder.tsx sets the relevant gaps to 0). `variant`
   ("default" | "compact", see InsertZone.tsx) no longer affects height —
   kept for any future divergence, currently only the plus glyph's size.

   An earlier version animated height on hover-intent (near-zero →
   expanded), damped by JS timers in InsertZone.tsx to avoid oscillation as
   rows/cards shifted under the pointer mid-transition. Both variants
   confirmed via testing that an *expanding* gap makes the plus itself a
   moving target, so there is no height rule left at all — reveal is
   opacity/tint only, via the `.band`/`.trigger` transitions below.
   `.expanded` (the class name) predates this change and is kept as-is
   rather than a disruptive rename — read it as "revealed".

   Keyboard reveal is `[data-interaction="keyboard"]:has(:focus-visible)`,
   not plain `:focus-within` or even plain `:has(:focus-visible)` alone.
   `:focus-within` matches DropdownMenu's programmatic refocus of its
   trigger after *any* close, including a mouse-driven one. `:focus-visible`
   alone should already exclude that (Astryx's own trigger outline is itself
   `:focus-visible`-scoped) but didn't reliably in testing — some browsers'
   heuristics still qualified that refocus. `[data-interaction="keyboard"]`
   (written directly onto the zone root by InsertZone.tsx, from tracked
   pointerdown/keydown/focus events — see its JSDoc) is the belt-and-suspenders
   layer: even a stray focus-visible match can't reveal anything after a
   pointer-driven interaction, regardless of what a future Astryx/browser
   change does to refocus timing. */

.zone {
  position: relative;
  height: 24px;
}

.zone.disabled {
  pointer-events: none;
}

.band {
  position: absolute;
  inset: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-inner);
  background: transparent;
  cursor: pointer;
  transition: background-color var(--duration-fast) var(--easing-default);
}

.zone.expanded .band,
.zone[data-interaction="keyboard"]:has(:focus-visible) .band {
  background: var(--color-overlay-hover);
}

.trigger {
  /* Bare glyph, no chip: no border, background, or circular clip — just
     Astryx's icon-only ghost button (kept for a11y: focusable, labeled),
     sized through the Icon's own `size` prop in InsertZone.tsx. Color is
     always "secondary" — it never swaps on hover. */
  flex-shrink: 0;
  opacity: 0;
  transition: opacity var(--duration-fast) var(--easing-default);
}

.zone.expanded .trigger,
.zone[data-interaction="keyboard"]:has(:focus-visible) .trigger {
  opacity: 1;
}

.zone.disabled .trigger {
  opacity: 0;
}

/* Neutralizes Astryx's ghost Button's own hover/active tint and
   `:focus-visible` outline for this trigger specifically — the rules above
   already own the entire reveal treatment (band tint + plus opacity), so
   the real button underneath should otherwise stay visually inert.

   Astryx's ghost variant (see Button.tsx's stylex `variants.ghost`) paints
   its hover/active state via `background-image` (a layered linear-gradient
   overlay), not `background-color`, and its focus ring via `outline` (not
   `box-shadow`) — each compiled by stylex into its own single-class +
   pseudo-class selector, e.g. `.x1a2b3c:hover { background-image: … }`,
   specificity (0,2,0). `.trigger button:hover` adds one type selector
   (`button`) on top of `.trigger`'s one class, landing at (0,2,1) — strictly
   higher on the tied class column — so it wins over Astryx's rule by
   specificity alone, in *any* source order, no `!important` needed. Same
   math for `:active` and `:focus-visible`. */
.trigger button:hover,
.trigger button:active {
  background-color: transparent;
  background-image: none;
}

.trigger button:focus-visible {
  outline: none;
}

/* Restores a keyboard-only focus ring — same token values Astryx's own
   ghost variant uses — gated by the identical `[data-interaction="keyboard"]`
   tracking as the band's reveal above, so a programmatic refocus after a
   pointer-driven selection can't paint a ring even if some browser's
   `:focus-visible` heuristic decides it qualifies. */
.zone[data-interaction="keyboard"] .trigger button:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 3px;
}

@media (prefers-reduced-motion: reduce) {
  .band {
    transition: none;
  }

  .trigger {
    transition: none;
  }
}
